import { wrap } from 'comlink/dist/esm/comlink.js' // eslint-disable-line no-restricted-syntax
import type { Remote } from 'comlink'
import { createPromise } from '@wix/thunderbolt-commons'
import { EditorFeatureConfigSymbol, ILogger, LoggerSymbol, PlatformEditorConfig, PlatformSiteConfig, SiteFeatureConfigSymbol, ViewerModel, ViewerModelSym } from '@wix/thunderbolt-symbols'
import { Manifest, ManifestSymbol, ViewerSourceSymbol } from 'thunderbolt-viewer-manager-types'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { name } from '../../symbols'

export const WorkerIframeWrapperManagerSymbol = Symbol('WorkerIframeWrapperManager')

export interface IWorkerIframeWrapperManager {
	createIframeWorker: ({ wixCodeBootstrapData }: { wixCodeBootstrapData: PlatformSiteConfig['bootstrapData']['wixCodeBootstrapData'] }) => Promise<any>
	disposeIframeWorker: () => void
}

export const WorkerIframeWrapperManager = withDependencies(
	[ViewerModelSym, ViewerSourceSymbol, ManifestSymbol, named(EditorFeatureConfigSymbol, name), named(SiteFeatureConfigSymbol, name), LoggerSymbol],
	(
		{ requestUrl }: ViewerModel,
		viewerSource: string,
		manifest: Manifest,
		{ serverName }: PlatformEditorConfig,
		platformSiteConfig: PlatformSiteConfig,
		logger: ILogger
	): IWorkerIframeWrapperManager & { platformWorkerPromise: Promise<HTMLIFrameElement> } => {
		let iframe: HTMLIFrameElement
		let workerPromise: Promise<Remote<Worker>>
		let iframeMessageHandler: (messageEvent: MessageEvent) => void
		const localIframeWorker = requestUrl.includes('localIframeWorker=true')
		const productionWorker = requestUrl.includes('productionWorker=true')
		const local = viewerSource.includes('localhost:5200')

		const disposeIframeWorker = () => {
			iframe?.remove()
			window.removeEventListener('message', iframeMessageHandler)
		}

		const getIframeSrc = ({ wixCodeBootstrapData }: { wixCodeBootstrapData: PlatformSiteConfig['bootstrapData']['wixCodeBootstrapData'] }) => {
			// webpack-runtime.js allows sharing the same registry between main thread and iframe.
			const suffix = `/workerIframeWrapper.html?localWorker=${local}&productionWorker=${productionWorker}&workerUrl=${platformSiteConfig.clientWorkerUrl}`
			const wixCodeOrDataBindingInstalled = !!wixCodeBootstrapData.wixCodeInstanceId

			if (localIframeWorker && !wixCodeOrDataBindingInstalled) {
				return `${viewerSource}${suffix}`
			}

			// when running locally, but wix code or data binding are installed, the iframe has to be served from prod url
			// because wix code apis are protected by CORS
			// in this case, tb version is pinned to some arbitrary working version.
			const thunderboltDsVersion = local ? '1.2700.0' : viewerSource
			const baseIframeSrc = wixCodeOrDataBindingInstalled ? `//${wixCodeBootstrapData.wixCodeInstanceId}.dev.${wixCodeBootstrapData.wixCloudBaseDomain}` : `//editor.${serverName}`
			return `${baseIframeSrc}/_partials/wix-thunderbolt-ds/${thunderboltDsVersion}${suffix}`
		}

		return {
			createIframeWorker: ({ wixCodeBootstrapData }) => {
				const iframeUrl = getIframeSrc({ wixCodeBootstrapData })
				if (iframe?.src === `https:${iframeUrl}`) {
					// keep iframe if URL did not change - iframe URL can change if dbsm/wix-code are installed (see logic in getIframeSrc function)
					return workerPromise
				}
				if (iframe) {
					iframe.remove()
				}
				iframe = window.document.createElement('iframe')
				iframe.style.cssText = 'position: fixed; left: 0; right: 0; top: 0; bottom: 0; width: 1px; height: 1px; background: transparent; border: 0'
				iframe.tabIndex = -1
				iframe.setAttribute('aria-hidden', 'true')

				// TODO kill prev iframe and return a new one upon invocation.
				iframe.src = iframeUrl

				const { promise, resolver: resolveWorker } = createPromise<Remote<Worker>>()
				workerPromise = promise

				iframeMessageHandler = ({ data, source }) => {
					if (source !== iframe.contentWindow) {
						return
					}
					switch (data.message) {
						case 'viewer platform worker port':
							const worker = wrap(data.port) as Remote<Worker>
							resolveWorker(worker)
							break
						case 'viewer platform worker error':
							logger.captureError(new Error(data.errorMessage), {
								tags: { feature: 'platform', worker: true },
							})
							break
					}
				}
				window.addEventListener('message', iframeMessageHandler)

				iframe.addEventListener('load', () => {
					iframe.contentWindow?.postMessage(
						{
							workerWrapperScript: manifest['workerIframeWrapper.js'],
						},
						'*'
					)
				})

				document.body.appendChild(iframe)

				return workerPromise
			},
			get platformWorkerPromise() {
				return workerPromise.then(() => iframe)
			},
			disposeIframeWorker,
		}
	}
)
